import { z } from 'zod';

/**
 * Phone + SMS-code sign-in, for coordinators.
 *
 * A SECOND auth slice rather than a widening of the first, deliberately. The
 * existing one models email + password with a cookie-borne refresh token, and
 * the web console still uses it; bending it to also mean "phone and a
 * six-digit code" would make every branch in it answer two questions at once.
 * They share the session store — one signed-in user — and nothing else.
 */

/**
 * Australian and Nepali mobiles, as the form accepts them.
 *
 * Deliberately generous: `0412 345 678`, `0412345678` and `+61412345678` are
 * one person, as are `981-2345678` and `+977 981 234 5678`, and a coordinator
 * typing their own number with spaces should not be told it is invalid.
 * Normalisation to E.164 happens on the SERVER — this rule is a form
 * validator, and the server's (`normaliseMobile`) is a parser for an untrusted
 * body. Same behaviour, two implementations, on purpose.
 */
// The app's phone fields send E.164 (`+61452136787`, `+9779812345678`); older
// callers send the nine digits after a fixed +61, `0452…` or `+61 452…`.
const MOBILE =
  /^(?:(?:\+?61\s?|0)?4\d{2}\s?\d{3}\s?\d{3}|(?:\+?977[\s-]?)?9[678]\d[\s-]?\d{3}[\s-]?\d{4})$/;

export const phoneInputSchema = z.object({
  phone: z.string().trim().regex(MOBILE, {
    message: 'Enter an Australian or Nepali mobile — +61 4XX XXX XXX or +977 98X XXX XXXX.',
  }),
});

export type PhoneInput = z.infer<typeof phoneInputSchema>;

export const verifyCodeInputSchema = z.object({
  phone: z.string().trim().min(1),
  /** Exactly six digits. The server will not accept anything else either. */
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, { message: 'Enter the six-digit code.' }),
  /**
   * "Remember this iPhone".
   *
   * A real server-side difference, not a client preference: on, the refresh
   * token gets a 30-day sliding life and is persisted; off, twelve hours,
   * non-sliding, and held in memory only.
   */
  remember: z.boolean().default(false),
  /**
   * What to call this device in the operator's device list.
   *
   * NOT decoration, and not optional in practice when `remember` is on: the
   * server creates a device row only when it receives a label, and a refresh
   * token with no device behind it is re-issued with the SHORT lifetime on its
   * first rotation. Sending `remember: true` without a label therefore buys a
   * session that quietly stops being remembered a few minutes in.
   *
   * Still optional in the schema because a client that does not offer
   * "remember this device" has nothing truthful to put here.
   */
  deviceLabel: z.string().trim().min(1).max(80).optional(),
  /** `ios`, `android`, `web`. Display only — the server never branches on it. */
  devicePlatform: z.string().trim().min(1).max(40).optional(),
});

export type VerifyCodeInput = z.infer<typeof verifyCodeInputSchema>;

/**
 * `POST /auth/request-code` → 202, always.
 *
 * IDENTICAL for a known and an unknown number, and nothing is sent to an
 * unknown one. The UI must therefore never claim delivery — it says "if that
 * number is registered", for the same reason the password-reset copy does.
 * The honest cost is a coordinator who mistypes and waits for nothing, which
 * is why "Stuck? We can help" needs to be a real contact before launch.
 */
export const codeRequestedSchema = z
  .object({
    status: z.literal('sent'),
    /** Seconds before another code may be requested. Drives the resend timer. */
    resend_available_in: z.number().int().nonnegative(),
    /** Seconds the code stays valid. The SMS copy promises ten minutes. */
    expires_in: z.number().int().positive(),
  })
  .transform((raw) => ({
    resendAvailableIn: raw.resend_available_in,
    expiresIn: raw.expires_in,
  }));

export type CodeRequested = z.infer<typeof codeRequestedSchema>;
